import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, PLAN_LABELS, SQUARE_PAYMENT_URL } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, User, Baby, CalendarDays, CalendarRange, CalendarClock, Calendar as CalendarIcon, Loader2, Camera, X, Sparkles } from "lucide-react";
import { resizeImageToBase64 } from "../lib/image";

const PLANS = [
  { id: "daily", label: "Diario", desc: "Un poquito cada día", icon: CalendarDays, color: "from-cyan-400 to-blue-600" },
  { id: "weekly", label: "Semanal", desc: "Cada 7 días", icon: CalendarRange, color: "from-green-400 to-emerald-600" },
  { id: "biweekly", label: "Quincenal", desc: "Cada 2 semanas", icon: CalendarClock, color: "from-orange-400 to-red-500" },
  { id: "monthly", label: "Mensual", desc: "Una vez al mes", icon: CalendarIcon, color: "from-purple-500 to-fuchsia-600" },
];

export default function RegistrationForm() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    emergency_contact: "",
    notes: "",
    birth_date: "",
    plan_type: "",
    photo: null,
  });
  const [photoLoading, setPhotoLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({ stripe_enabled: false, square_enabled: true });

  useEffect(() => {
    api.get("/payments/methods").then(({ data }) => setPaymentMethods(data)).catch(() => {});
  }, []);

  // Compute age + category locally for instant preview
  const computeAge = (bd) => {
    if (!bd) return null;
    const today = new Date();
    const d = new Date(bd);
    if (isNaN(d.getTime())) return null;
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
  };
  const computeCategory = (age) => {
    if (age == null || age < 3) return null;
    if (age <= 6) return { key: "small", price: 55, label: "3-6 años" };
    if (age <= 11) return { key: "child", price: 150, label: "7-11 años" };
    return { key: "adult", price: 250, label: "12+ años" };
  };
  const userAge = computeAge(form.birth_date);
  const userCategory = computeCategory(userAge);

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error("La imagen es muy grande (máx 12MB)");
      return;
    }
    setPhotoLoading(true);
    update("photo", null); // clear any stale photo while processing
    try {
      const dataUrl = await resizeImageToBase64(file, 400, 0.85);
      if (!dataUrl) throw new Error("No se pudo procesar la imagen");
      update("photo", dataUrl);
      toast.success("Foto cargada correctamente");
    } catch (err) {
      console.error("[photo upload]", err);
      const msg = err?.message || "No se pudo procesar la imagen";
      toast.error(msg, { duration: 8000 });
      update("photo", null);
    } finally {
      setPhotoLoading(false);
      // Reset input so picking the same file again re-triggers onChange
      if (e?.target) e.target.value = "";
    }
  };

  // Update preview when birth_date/plan changes
  useEffect(() => {
    const loadPreview = async () => {
      if (!form.birth_date || !form.plan_type || !userCategory) return setPreview(null);
      try {
        const { data } = await api.post(
          `/plan/preview?birth_date=${form.birth_date}&plan_type=${form.plan_type}`
        );
        setPreview(data);
      } catch (e) {
        console.error(e);
      }
    };
    loadPreview();
  }, [form.birth_date, form.plan_type, userCategory?.key]);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const validateStep1 = () => {
    if (!form.full_name || form.full_name.length < 2) return "Ingresa tu nombre completo";
    if (!form.email || !form.email.includes("@")) return "Email inválido";
    if (!form.phone || form.phone.length < 7) return "Teléfono inválido";
    return null;
  };

  const goNext = () => {
    if (step === 1) {
      const err = validateStep1();
      if (err) {
        toast.error(err);
        return;
      }
    }
    if (step === 2) {
      if (!form.birth_date) {
        toast.error("Ingresa tu fecha de nacimiento");
        return;
      }
      if (!userAge || userAge < 3) {
        toast.error("La edad mínima para inscribirse es 3 años");
        return;
      }
    }
    if (step === 3 && !form.plan_type) {
      toast.error("Selecciona un plan de pago");
      return;
    }
    setStep((s) => Math.min(s + 1, 4));
  };

  const submit = async (paymentMethod = "square") => {
    setLoading(true);
    try {
      // Create registration
      const { data: reg } = await api.post("/registrations", form);
      localStorage.setItem("last_registration_id", reg.id);

      if (paymentMethod === "cash") {
        toast.success("¡Inscripción confirmada! Paga en efectivo en la iglesia.");
        window.location.href = `/mi-cuenta/${reg.id}?cash=1`;
        return;
      }

      // Stripe Checkout — supports Apple Pay / Google Pay / Cash App / cards automatically
      toast.success("¡Inscripción creada! Te llevamos al pago seguro.");
      try {
        const { data: checkout } = await api.post("/payments/checkout", {
          registration_id: reg.id,
          installment_number: 1,
          origin_url: window.location.origin,
        });
        if (checkout?.url) {
          window.location.href = checkout.url;
          return;
        }
      } catch (payErr) {
        console.error("Stripe checkout failed:", payErr);
        const detail = payErr?.response?.data?.detail;
        if (payErr?.response?.status === 503) {
          // Stripe disabled — gracefully fallback to Square
          toast.error(detail || "Pago con tarjeta no disponible. Te llevamos a Square.", { duration: 7000 });
          // Pre-fill reference and open Square
          const ref = `Campista ${reg.camper_number_display || ""} - ${reg.full_name}`;
          if (navigator.clipboard) {
            try { await navigator.clipboard.writeText(ref); } catch (_) {}
          }
          setTimeout(() => { window.location.href = SQUARE_PAYMENT_URL; }, 2000);
          return;
        }
      }
      // Fallback: send to their account page so they can retry
      window.location.href = `/mi-cuenta/${reg.id}`;
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al procesar inscripción";
      toast.error(typeof msg === "string" ? msg : "Error inesperado");
      setLoading(false);
    }
  };

  return (
    <section id="inscripcion" className="relative bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50 py-24 lg:py-32 px-6 lg:px-12">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-black text-yellow-400 text-xs uppercase tracking-[0.4em] font-bold mb-6">
            Reserva tu espacio
          </span>
          <h2 className="font-heading uppercase text-5xl sm:text-7xl text-black leading-[0.9] tracking-tighter">
            Reserva tu<br />
            <span className="text-orange-600">espacio ya</span>
          </h2>
          <p className="mt-6 text-lg text-gray-700">
            Asegura tu lugar y elige el plan de pago que mejor se adapte a ti.
          </p>

          {/* Weekly plan highlight banner */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-6 inline-flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full shadow-lg shadow-green-500/30"
            data-testid="weekly-recommendation-banner"
          >
            <Sparkles className="w-5 h-5" />
            <span className="text-sm sm:text-base font-semibold">
              💡 Con el plan <span className="underline decoration-2 underline-offset-2">semanal</span> tendrás todo pagado <strong>1 semana antes</strong> del campamento
            </span>
          </motion.div>
        </motion.div>

        {/* Progress bar */}
        <div className="flex items-center justify-center gap-2 mb-8" data-testid="form-progress">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex items-center">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                  n <= step ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-400"
                }`}
              >
                {n < step ? <Check className="w-4 h-4" /> : n}
              </div>
              {n < 4 && <div className={`w-12 h-1 ${n < step ? "bg-orange-500" : "bg-gray-200"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-6 lg:p-10 border border-gray-100">
          <AnimatePresence mode="wait">
            {/* STEP 1: Personal info */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <h3 className="font-heading uppercase text-3xl text-black mb-6">Tus datos</h3>

                {/* Photo upload */}
                <div className="flex items-center gap-5">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full border-4 border-orange-400 bg-gradient-to-br from-yellow-100 to-orange-100 flex items-center justify-center overflow-hidden shadow-lg">
                      {form.photo ? (
                        <img src={form.photo} alt="avatar" className="w-full h-full object-cover" />
                      ) : photoLoading ? (
                        <Loader2 className="w-7 h-7 text-orange-500 animate-spin" />
                      ) : (
                        <Camera className="w-8 h-8 text-orange-500" />
                      )}
                    </div>
                    {form.photo && (
                      <button
                        type="button"
                        onClick={() => update("photo", null)}
                        data-testid="form-remove-photo"
                        className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md"
                        aria-label="Quitar foto"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className="block mb-2 font-semibold">Foto de perfil <span className="text-gray-400 font-normal">(opcional)</span></Label>
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg cursor-pointer transition-colors text-sm font-semibold">
                      <Camera className="w-4 h-4" />
                      {form.photo ? "Cambiar foto" : "Subir foto"}
                      <input
                        type="file"
                        accept="image/*"
                        capture="user"
                        onChange={onPickPhoto}
                        className="hidden"
                        data-testid="form-input-photo"
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-2">Aparecerás en la lista pública de la familia inscrita.</p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="full_name">Nombre completo *</Label>
                  <Input
                    id="full_name"
                    data-testid="form-input-fullname"
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    placeholder="Juan Pérez"
                    className="mt-2 h-12"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      data-testid="form-input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="tu@email.com"
                      className="mt-2 h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Teléfono / WhatsApp *</Label>
                    <Input
                      id="phone"
                      data-testid="form-input-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="614-508-0303"
                      className="mt-2 h-12"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="emergency_contact">Contacto de emergencia (opcional)</Label>
                  <Input
                    id="emergency_contact"
                    data-testid="form-input-emergency"
                    value={form.emergency_contact}
                    onChange={(e) => update("emergency_contact", e.target.value)}
                    placeholder="Nombre y teléfono"
                    className="mt-2 h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notas (opcional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="form-input-notes"
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="Alergias, restricciones alimentarias, etc."
                    className="mt-2"
                  />
                </div>
              </motion.div>
            )}

            {/* STEP 2: Birth date → auto-detect category */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="font-heading uppercase text-3xl text-black mb-2">Tu fecha de nacimiento</h3>
                <p className="text-gray-600 mb-6 text-sm">El precio se determina automáticamente según tu edad.</p>

                <div className="max-w-sm">
                  <Label htmlFor="birth_date">Fecha de nacimiento *</Label>
                  <Input
                    id="birth_date"
                    data-testid="form-input-birthdate"
                    type="date"
                    value={form.birth_date}
                    onChange={(e) => update("birth_date", e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    className="mt-2 h-12 text-lg"
                  />
                </div>

                {/* Pricing tiers reference */}
                <div className="mt-6 grid sm:grid-cols-3 gap-3">
                  {[
                    { id: "small", label: "3-6 años", price: 55, color: "from-cyan-400 to-blue-500" },
                    { id: "child", label: "7-11 años", price: 150, color: "from-green-400 to-emerald-500" },
                    { id: "adult", label: "12+ años", price: 250, color: "from-orange-500 to-red-600" },
                  ].map((t) => {
                    const active = userCategory?.key === t.id;
                    return (
                      <div
                        key={t.id}
                        className={`p-4 rounded-2xl border-2 transition-all ${
                          active
                            ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200 scale-105"
                            : "border-gray-200 opacity-60"
                        }`}
                        data-testid={`tier-${t.id}`}
                      >
                        <div className="text-xs uppercase tracking-widest text-gray-600 font-bold">{t.label}</div>
                        <div className={`mt-1 font-heading text-3xl ${active ? "text-gradient-gold" : "text-gray-700"}`}>${t.price}</div>
                      </div>
                    );
                  })}
                </div>

                {form.birth_date && userCategory && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-5 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl text-black"
                    data-testid="age-detection-result"
                  >
                    <div className="text-xs uppercase tracking-widest font-bold mb-1">Tu inscripción</div>
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-heading text-3xl">{userAge} años</div>
                        <div className="text-sm font-semibold">Categoría {userCategory.label}</div>
                      </div>
                      <div className="font-heading text-5xl">${userCategory.price}</div>
                    </div>
                  </motion.div>
                )}

                {form.birth_date && !userCategory && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    La edad mínima para inscribirse es 3 años.
                  </div>
                )}
              </motion.div>
            )}

            {/* STEP 3: Payment plan */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="font-heading uppercase text-3xl text-black mb-2">Tu plan de pago</h3>
                <p className="text-gray-600 mb-6">Elige cómo quieres pagar tu inscripción hasta agosto 28, 2026.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {PLANS.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`plan-${p.id}`}
                      onClick={() => update("plan_type", p.id)}
                      className={`relative p-6 rounded-2xl border-2 transition-all text-left card-hover ${
                        form.plan_type === p.id
                          ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {p.id === "weekly" && (
                        <span className="absolute -top-3 right-4 px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-md">
                          ✦ Recomendado
                        </span>
                      )}
                      <div className={`inline-flex w-12 h-12 rounded-xl bg-gradient-to-br ${p.color} items-center justify-center mb-4`}>
                        <p.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="font-heading uppercase text-xl text-black">{p.label}</div>
                      <div className="text-sm text-gray-600 mt-1">{p.desc}</div>
                      {p.id === "weekly" && (
                        <div className="mt-3 text-xs text-green-700 font-semibold">✓ Todo pagado 1 semana antes</div>
                      )}
                    </button>
                  ))}
                </div>

                {preview && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-5 bg-black text-white rounded-2xl"
                    data-testid="plan-preview"
                  >
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-yellow-400 mb-1">Cuota {PLAN_LABELS[preview.plan_type]?.toLowerCase()}</div>
                        <div className="font-heading text-4xl text-gradient-gold">${preview.installment_amount}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs uppercase tracking-widest text-white/60 mb-1">x {preview.num_installments} cuotas</div>
                        <div className="text-sm text-white/80">Total: <span className="text-yellow-400 font-bold">${preview.total_amount}</span></div>
                      </div>
                    </div>
                    {preview.payment_deadline && (
                      <div className="mt-4 pt-4 border-t border-white/10 text-xs text-white/60">
                        ✓ Todo quedará pagado el <span className="text-yellow-400 font-semibold">{new Date(preview.payment_deadline).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}</span> (1 semana antes del campamento)
                      </div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* STEP 4: Confirmation */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <h3 className="font-heading uppercase text-3xl text-black mb-6">Confirma tu inscripción</h3>
                <div className="space-y-3 p-5 bg-gray-50 rounded-xl">
                  <Row label="Nombre" value={form.full_name} />
                  <Row label="Email" value={form.email} />
                  <Row label="Teléfono" value={form.phone} />
                  <Row label="Fecha de nacimiento" value={form.birth_date} />
                  <Row label="Edad" value={`${userAge} años`} />
                  <Row label="Categoría" value={userCategory?.label || ""} />
                  <Row label="Plan" value={PLAN_LABELS[form.plan_type]} />
                </div>
                {preview && (
                  <div className="p-5 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl text-black" data-testid="confirmation-summary">
                    <div className="text-xs uppercase tracking-widest font-bold mb-2">Total a pagar</div>
                    <div className="font-heading text-5xl">${preview.total_amount}</div>
                    <div className="mt-2 text-sm font-semibold">
                      Pagarás ${preview.installment_amount} {PLAN_LABELS[preview.plan_type]?.toLowerCase()} por {preview.num_installments} cuotas
                    </div>
                  </div>
                )}
                <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                  <p className="text-sm text-green-900 font-semibold mb-1">
                    💳 Pago seguro con Square
                  </p>
                  <p className="text-xs text-green-800">
                    Al confirmar serás redirigido a Square para pagar tu <span className="font-bold">primera cuota de ${preview?.installment_amount}</span>. Square cobrará automáticamente tu plan {PLAN_LABELS[form.plan_type]?.toLowerCase()} hasta completar el total. Acepta tarjeta, Apple Pay y Google Pay.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-8">
            {step < 4 ? (
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  disabled={step === 1 || loading}
                  onClick={() => setStep((s) => Math.max(s - 1, 1))}
                  data-testid="form-back-button"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Atrás
                </Button>
                <Button
                  onClick={goNext}
                  disabled={photoLoading}
                  data-testid="form-next-button"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wider px-8"
                >
                  {photoLoading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Procesando foto...</> : <>Siguiente <ChevronRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <Button
                  onClick={() => submit("square")}
                  disabled={loading || photoLoading}
                  data-testid="form-submit-button"
                  size="lg"
                  className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold uppercase tracking-wider h-14 text-base"
                >
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</> : photoLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cargando foto...</> : paymentMethods.stripe_enabled ? <>💳 Pagar con tarjeta / Apple Pay</> : <>💳 Pagar con Square (tarjeta)</>}
                </Button>
                <Button
                  onClick={() => submit("cash")}
                  disabled={loading || photoLoading}
                  data-testid="form-submit-cash-button"
                  variant="outline"
                  size="lg"
                  className="w-full border-2 border-black text-black hover:bg-black hover:text-white font-bold uppercase tracking-wider h-14 text-base"
                >
                  {photoLoading ? "Cargando foto..." : "💵 Pagaré en efectivo en la iglesia"}
                </Button>
                <Button
                  variant="ghost"
                  disabled={loading}
                  onClick={() => setStep(3)}
                  data-testid="form-back-button"
                  className="w-full"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Atrás
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  Si eliges efectivo: tu inscripción queda reservada. Tu próxima cuota la podrás pagar online o en persona.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-gray-500 uppercase tracking-wider text-xs">{label}</span>
      <span className="text-black font-semibold">{value}</span>
    </div>
  );
}
